import React, { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { fetchClientPlacements } from "../../../services/clientPlacementService";
import { ClientPlacementListItem } from "../../../types/fyo";
import PageHeader from "../../shared/PageHeader/PageHeader";
import { formatMonthYear } from "../../../utils/formatters";
import "./ClientPlacementPage.css";

const LIMIT = 50;

interface Props {
  onViewCandidate: (id: number) => void;
  onSelect:        (id: number) => void;
}

export default function ClientPlacementPage({ onViewCandidate, onSelect }: Props) {
  const location = useLocation();
  const preset = (location.state as { filters?: { status?: string } } | null)?.filters;

  const [rows,    setRows]    = useState<ClientPlacementListItem[]>([]);
  const [total,   setTotal]   = useState(0);
  const [offset,  setOffset]  = useState(0);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState<string | null>(null);

  const [filterStatus,        setFilterStatus]        = useState(preset?.status ?? "placed");
  const [filterName,          setFilterName]          = useState("");
  const [filterWindowExpired, setFilterWindowExpired] = useState("");

  const [applied, setApplied] = useState({
    status: preset?.status ?? "placed", name: "", windowExpired: "",
  });

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    fetchClientPlacements({
      status:         applied.status        || undefined,
      name:           applied.name          || undefined,
      window_expired: applied.windowExpired === "true"  ? true
                    : applied.windowExpired === "false" ? false
                    : undefined,
      limit:  LIMIT,
      offset,
    })
      .then((r: any) => { setRows(r.client_placements ?? r.items ?? []); setTotal(r.total ?? 0); })
      .catch((e: any) => setError(e.message))
      .finally(() => setLoading(false));
  }, [applied, offset]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="fyo-page">
      <PageHeader title="LinkedIn Tracked FYO Company Hires" subtitle="Candidate-to-client placement tracking" />

      <div className="fyo-filter-bar">
        <input
          className="fyo-input"
          placeholder="Search by name"
          value={filterName}
          onChange={(e) => setFilterName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && setApplied({ status: filterStatus, name: filterName, windowExpired: filterWindowExpired })}
          style={{ width: 200 }}
        />
        <select className="fyo-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
          <option value="">All Statuses</option>
          <option value="active">Active</option>
          {/* <option value="paused">Untrackable</option> */}
          <option value="placed">Hired In Ohio</option>
          {/* <option value="not_placed">Tracking Expired</option> */}
        </select>

        <select className="fyo-select" value={filterWindowExpired} onChange={(e) => setFilterWindowExpired(e.target.value)}>
          <option value="">All Tracking Statuses</option>
          <option value="false">Tracking Active</option>
          <option value="true">Tracking Expired</option>
        </select>

        <button className="fyo-btn fyo-btn--primary" onClick={() => {
          setApplied({ status: filterStatus, name: filterName, windowExpired: filterWindowExpired });
          setOffset(0);
        }}>Apply</button>
        <button className="fyo-btn fyo-btn--ghost" onClick={() => {
          setFilterStatus(""); setFilterName(""); setFilterWindowExpired("");
          setApplied({ status: "", name: "", windowExpired: "" });
          setOffset(0);
        }}>Clear</button>
      </div>

      {error && <div className="fyo-error-banner">{error}</div>}

      <div className="fyo-card fyo-card--no-pad">
        {loading ? (
          <p className="state-text state-text--muted" style={{ padding: "var(--space-6)" }}>Loading...</p>
        ) : (
          <>
            <div className="fyo-table-scroll">
              <table className="fyo-table">
              <thead>
                <tr>
                  <th>Candidate Name</th>
                  <th>Ohio Employer</th>
                  <th>Location</th>
                  <th>Date Qualified</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={4} className="fyo-muted" style={{ textAlign: "center", padding: "var(--space-6)" }}>No records found.</td></tr>
                ) : rows.map((r, i) => (
                  <tr
                    key={`${r.rms_candidate_id}-${i}`}
                    className={`fyo-table__row--clickable ${i % 2 === 1 ? "fyo-table__row--odd" : ""}`}
                    onClick={() => onSelect(r.rms_candidate_id)}
                  >
                    <td className="fyo-table__cell--primary">
                      <button
                        className="fyo-link-btn"
                        onClick={(e) => { e.stopPropagation(); onViewCandidate(r.rms_candidate_id); }}
                      >
                        {r.candidate_name}
                      </button>
                      {r.candidate_email && (
                        <div style={{ fontSize: "var(--font-size-xs)", color: "var(--color-text-tertiary)", marginTop: 2 }}>
                          {r.candidate_email}
                        </div>
                      )}
                    </td>
                    <td>{r.matched_company_name || "—"}</td>
                    <td>{r.matched_job_location || "—"}</td>
                    <td>{r.status === "placed" ? formatMonthYear(r.updated_at) : "—"}</td>
                  </tr>
                ))}
              </tbody>
              </table>
            </div>

            <div className="fyo-pagination">
              <span className="fyo-pagination__info">
                {total === 0 ? "No results" : `${offset + 1}–${Math.min(offset + LIMIT, total)} of ${total}`}
              </span>
              <button className="fyo-btn fyo-btn--ghost fyo-btn--sm" disabled={offset === 0}
                onClick={() => setOffset((o) => Math.max(0, o - LIMIT))}>Prev</button>
              <span className="fyo-muted">Page {Math.floor(offset / LIMIT) + 1} of {Math.ceil(total / LIMIT) || 1}</span>
              <button className="fyo-btn fyo-btn--ghost fyo-btn--sm" disabled={offset + LIMIT >= total}
                onClick={() => setOffset((o) => o + LIMIT)}>Next</button>
            </div>
          </>
        )}
      </div>

    </div>
  );
}
